import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from 'src/app/services/api.service';
import { UtilService } from 'src/app/services/util.service';
import { NotificationDetailsComponent } from './notification-details/notification-details.component';
import { AlertService } from 'src/app/services/alert.service';
import { forkJoin } from 'rxjs';

type FilterStatus = 'all' | 'Read' | 'UnRead';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss']
})
export class NotificationsComponent implements OnInit {

  announcements:any = [];
  notifications:any = [];
  activeTab = 'announcements';
  filterStatus : FilterStatus = "UnRead"; // Default to UnRead

  constructor(
    private api: ApiService, 
    public util:UtilService, 
    private readonly alertService: AlertService,
    public dialog: MatDialog) { }

  ngOnInit(): void {
    this.loadAnnouncements();
    this.loadNotifications();
  }

  loadAnnouncements() {
    this.api.getAnnouncements().subscribe((res:any)=>{
      this.announcements = res;
    });
  }

  loadNotifications() {
    this.api.getNotifications().subscribe((res:any)=>{
      this.notifications = res;
    });
  }

  setActiveTab(tab:string){
    this.activeTab = tab;
  }


  deleteNotification(index:number){
    if (this.activeTab === 'announcements') {
      const announcement = this.announcements[index];
      this.announcements.splice(index, 1);
      this.api.deleteAnnouncement(announcement.announcementId).subscribe({
        next: () => {
          this.alertService.toast('Announcement deleted successfully.', 'success');
        },
        error: (error) => {
          this.alertService.toast('Error deleting announcement.', 'error');
          this.loadAnnouncements();
        }
      });
    } else if (this.activeTab === 'notifications') {
      const notification = this.notifications[index];
      this.notifications.splice(index, 1);
      this.api.deleteNotification(notification.pushNotificationId).subscribe({
        next: () => {
          this.alertService.toast('Notification deleted successfully.', 'success');
        },
        error: (error) => {
          this.alertService.toast('Error deleting notification.', 'error');
          this.loadNotifications();
        }
      });
    }
  }

  openNotification(notification:any){
    this.dialog.open(NotificationDetailsComponent, {
      width: '60vw',
      minWidth: '60vw',
      data: {notification, activeTab: this.activeTab},
    }).afterClosed().subscribe(this.ngOnInit.bind(this));
  }

  // Add these methods
  setFilterStatus(status: 'Read' | 'UnRead') {
    this.filterStatus = status;
  }

  clearAll() {
    // Implementation for clearing all notifications
    if (this.activeTab === 'announcements') {
      const UnReadAnnouncements = this.getFilteredAnnouncements("UnRead");
      if (UnReadAnnouncements.length > 0) {
        const apiCalls = UnReadAnnouncements.map(announcement => 
          this.api.readAnnouncement(announcement.announcementId)
        );
        
        forkJoin(apiCalls).subscribe({
          next: () => {
            this.loadAnnouncements();
            this.alertService.toast('All UnRead announcements have been marked as Read.', 'success');
          },
          error: (error) => {
            this.alertService.toast('Error marking announcements as Read.', 'error');
          }
        });
      }
    }
    if (this.activeTab === 'notifications') {
      const UnReadNotifications = this.getFilteredNotifications("UnRead");
      if (UnReadNotifications.length > 0) {
        const apiCalls = UnReadNotifications.map(notification => 
          this.api.readNotification(notification.pushNotificationId)
        );
        
        forkJoin(apiCalls).subscribe({
          next: () => {
            this.loadNotifications();
            this.alertService.toast('All UnRead notifications have been marked as Read.', 'success');
          },
          error: (error) => {
            this.alertService.toast('Error marking notifications as Read.', 'error');
          }
        });
      }
    }
  }

  getFilteredAnnouncements(filterStatus: FilterStatus = this.filterStatus) {
    if (filterStatus === 'Read') {
      return this.announcements.filter(announcement => announcement.announcementStatus === 'Read');
    }
    if (filterStatus === 'UnRead') {
      return this.announcements.filter(announcement => announcement.announcementStatus !== 'Read');
    }
    return this.announcements;
  }

  getFilteredNotifications(filterStatus: FilterStatus = this.filterStatus) {
    if (filterStatus === 'Read') {
      return this.notifications.filter(notification => notification.pushNotificationStatusName === 'Read');
    }
    if (filterStatus === 'UnRead') {
      return this.notifications.filter(notification => notification.pushNotificationStatusName !== 'Read');
    }
    return this.notifications;
  }

  // Count methods for badges
  getUnReadAnnouncementsCount(): number {
    return this.getFilteredAnnouncements('UnRead').length;
  }

  getReadAnnouncementsCount(): number {
    return this.getFilteredAnnouncements('Read').length;
  }

  getUnReadNotificationsCount(): number {
    return this.getFilteredNotifications('UnRead').length;
  }

  getReadNotificationsCount(): number {
    return this.getFilteredNotifications('Read').length;
  }
}
